/**
 * Whether the company details point the add-in at hosted Pusher rather than a Reverb server.
 */
function usesPusherCloud(details) {
  return details.broadcast_driver === "pusher" ||
    (!details.broadcast_driver && !details.reverb_host && Boolean(details.pusher_cluster));
}

/**
 * Returns a pusher_* field only for details that predate broadcast_driver, when servers reused those fields for Reverb.
 */
function legacyField(details, field) {
  return details.broadcast_driver ? undefined : details[field];
}

/**
 * Returns the app key of the service the company details point the add-in at.
 */
export function connectionKey(details) {
  return usesPusherCloud(details) ? details.pusher_key : details.reverb_key || legacyField(details, "pusher_key");
}

export function buildConnectionOptions(details, { environment, environmentURL, apiKey, localProxy, location }) {
  const isPusherCloud = usesPusherCloud(details);
  const host = isPusherCloud ? "" : details.reverb_host || legacyField(details, "pusher_host") || "";
  const cluster = isPusherCloud ? details.pusher_cluster : legacyField(details, "pusher_cluster");

  const options = {
    // pusher-js throws when this is missing, even with an explicit host.
    cluster: cluster || "",
    activityTimeout: 30000,
    pongTimeout: 10000,
    authEndpoint: `${environmentURL}/broadcasting/auth`,
    auth: {
      headers: {
        "Authorization": `Bearer ${apiKey}`
      }
    }
  };

  if (!isPusherCloud && localProxy && environment === "local") {
    options.wsHost = location.hostname;
    options.wsPort = Number(location.port) || 443;
    options.wssPort = options.wsPort;
    options.wsPath = "/reverb";
    options.forceTLS = true;
    options.enabledTransports = ["ws", "wss"];
    return options;
  }

  if (!host) {
    return options;
  }

  const secure = (details.reverb_scheme || "https") === "https";
  const port = Number(details.reverb_port) || (secure ? 443 : 80);

  options.wsHost = host;
  options.wsPort = port;
  options.wssPort = port;
  options.forceTLS = secure;
  // Reverb offers no SockJS or HTTP fallback, so leaving those enabled only
  // spends the retry budget on transports that can never connect.
  options.enabledTransports = ["ws", "wss"];

  return options;
}
